import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, TrendingDown, AlertTriangle, CheckCircle, Package, Clock, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ReplenishmentDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState([]);
  const [summary, setSummary] = useState({});
  const [settings, setSettings] = useState({});
  const [currentPeriod, setCurrentPeriod] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [triggering, setTriggering] = useState(false);
  const [triggeringSku, setTriggeringSku] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/wms/replenishment/dashboard');
      setDashboardData(response.data.dashboard || []);
      setSummary(response.data.summary || {});
      setSettings(response.data.settings || {});
      setCurrentPeriod(response.data.current_period || {});
    } catch (error) {
      toast.error('Failed to fetch dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerAll = async () => {
    try {
      setTriggering(true);
      const response = await apiClient.post('/wms/pick-slips/trigger-manual');
      toast.success(response.data.message);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger replenishment');
    } finally {
      setTriggering(false);
    }
  };

  const handleTriggerSku = async (skuCode) => {
    try {
      setTriggeringSku(skuCode);
      const response = await apiClient.post(`/wms/pick-slips/trigger-sku/${skuCode}`);
      toast.success(response.data.message);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger replenishment');
    } finally {
      setTriggeringSku(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      NEEDS_REPLENISHMENT: { color: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'Needs Replenishment' },
      SLIP_PENDING: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Slip Pending' },
      LOW_STOCK: { color: 'bg-yellow-100 text-yellow-700', icon: TrendingDown, label: 'Low Stock' },
      ADEQUATE: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Adequate' }
    };
    const { color, icon: Icon, label } = config[status] || config.ADEQUATE;
    return (
      <Badge className={`${color} font-medium`}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  const filteredData = dashboardData.filter(item =>
    item.sku_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.design_name && item.design_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Replenishment Dashboard</h1>
              <p className="text-sm text-[#E8DCC8]">
                Target: {currentPeriod.month_name} {currentPeriod.year}
              </p>
            </div>
          </div>
          <Button
            onClick={handleTriggerAll}
            disabled={triggering}
            className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
            data-testid="trigger-all-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${triggering ? 'animate-spin' : ''}`} />
            Trigger All
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Needs Replenishment</p>
                <p className="text-3xl font-bold text-red-700">{summary.needs_replenishment || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Pending Pick Slips</p>
                <p className="text-3xl font-bold text-blue-700">{summary.pending_slips || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-blue-400" />
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">Low Stock</p>
                <p className="text-3xl font-bold text-yellow-700">{summary.low_stock || 0}</p>
              </div>
              <TrendingDown className="w-10 h-10 text-yellow-400" />
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Adequate Stock</p>
                <p className="text-3xl font-bold text-green-700">{summary.adequate || 0}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </div>
        </div>

        {/* Auto Trigger Settings */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BarChart3 className="w-5 h-5 text-[#5A5F6E]" />
              <div>
                <p className="font-medium text-[#1A1F2E]">Auto Replenishment</p>
                <p className="text-sm text-[#5A5F6E]">
                  {settings.auto_trigger_enabled ? (
                    <>Enabled - Daily at {settings.auto_trigger_time}</>
                  ) : (
                    'Disabled'
                  )}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/wms/settings')}
            >
              Configure
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by SKU or design name"
            className="max-w-md"
          />
        </div>

        {/* SKU Table */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#5A5F6E]">Loading...</div>
          ) : filteredData.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
              <p className="text-[#5A5F6E]">No SKU data available.</p>
              <p className="text-sm text-[#5A5F6E] mt-1">Upload projections to see replenishment status.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-[#E5DFD0]">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">SKU</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Design</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Class</th>
                    <th className="px-4 py-3 text-right font-medium text-[#1A1F2E]">FG Stock</th>
                    <th className="px-4 py-3 text-right font-medium text-[#1A1F2E]">WIP</th>
                    <th className="px-4 py-3 text-right font-medium text-[#1A1F2E]">Available</th>
                    <th className="px-4 py-3 text-right font-medium text-[#1A1F2E]">SFG Stock</th>
                    <th className="px-4 py-3 text-right font-medium text-[#1A1F2E]">Projection</th>
                    <th className="px-4 py-3 text-right font-medium text-[#1A1F2E]">Trigger At</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Status</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item) => (
                    <tr key={item.sku_code} className="border-b border-[#E5DFD0] hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-[#1A1F2E]">{item.sku_code}</td>
                      <td className="px-4 py-3 text-[#5A5F6E]">{item.design_name || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`${
                          item.velocity_class === 'A' ? 'bg-green-100 text-green-700' :
                          item.velocity_class === 'B' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {item.velocity_class || 'C'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{item.fg_stock.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono text-blue-600">{(item.wip_stock || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">{(item.available_stock ?? item.fg_stock).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono">{item.sfg_stock.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono">{item.next_month_projection.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#5A5F6E]">
                        ≤ {item.trigger_point.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(item.status)}</td>
                      <td className="px-4 py-3 text-center">
                        {item.pending_slip ? (
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => navigate(`/wms/picker-guidance?slip=${item.pending_slip}`)}
                            className="text-blue-600"
                          >
                            {item.pending_slip}
                          </Button>
                        ) : item.status === 'NEEDS_REPLENISHMENT' ? (
                          <Button
                            size="sm"
                            onClick={() => handleTriggerSku(item.sku_code)}
                            disabled={triggeringSku === item.sku_code}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            {triggeringSku === item.sku_code ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              'Trigger'
                            )}
                          </Button>
                        ) : (
                          <span className="text-[#5A5F6E]">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ReplenishmentDashboard;
